import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import StatueProductCard from '../components/StatueProductCard';

const MixedArtGallery = () => {
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [mixedArt, setMixedArt] = useState([]);
  const [loading, setLoading] = useState(true);
  const titleRef = useRef(null);
  const [isTitleVisible, setIsTitleVisible] = useState(false);

  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/products/mixed-art`);
        if (response.ok) {
          const data = await response.json();
          setMixedArt(data);
        }
      } catch (error) {
        console.error('Failed to fetch statues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [apiUrl]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsTitleVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12 flex items-center justify-center">
        <div className="text-[hsl(45,100%,70%)] text-2xl">Loading statues...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button - Positioned to avoid music player overlap */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group ml-52"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Header */}
        <div
          ref={titleRef}
          className={`text-center mb-12 transition-all duration-1000 ${
            isTitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Mixed Art <span className="text-[hsl(45,100%,70%)]">Pieces</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
            Explore our unique collection of mixed art pieces, featuring innovative designs and contemporary expressions of artistic creativity.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            {mixedArt.length} {mixedArt.length === 1 ? 'piece' : 'pieces'} available
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="wait">
            {mixedArt.map((product, index) => {
              const currentIndex = mixedArt.findIndex(p => p.id === expandedProductId);
              const handlePreviousProduct = () => {
                const prevIndex = currentIndex === 0 ? mixedArt.length - 1 : currentIndex - 1;
                setExpandedProductId(mixedArt[prevIndex].id);
              };
              const handleNextProduct = () => {
                const nextIndex = (currentIndex + 1) % mixedArt.length;
                setExpandedProductId(mixedArt[nextIndex].id);
              };

              return (
                <StatueProductCard 
                  key={product.id} 
                  product={product} 
                  index={index}
                  onExpand={setExpandedProductId}
                  isExpanded={expandedProductId === product.id}
                  onClose={() => setExpandedProductId(null)}
                  onPreviousProduct={handlePreviousProduct}
                  onNextProduct={handleNextProduct}
                  hasMultipleProducts={mixedArt.length > 1}
                />
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {mixedArt.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No statues available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MixedArtGallery;
